import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextField } from 'components/Field/TextField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { randomKey } from 'lib/riskbase';
import * as React from 'react';
import { Keyboard } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  eventSelectScreenSelection,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/app/actions';
import { REMOTE_CREATE_ASSET } from 'store/remote/actions';
import { APIRequestCreateAsset } from 'store/remote/types';

export const CreateAssetScreen: React.FC<
  ModalScreenProps<ModalScreens.CREATEASSET>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const { organisations } = route.params;

  const eventKeyOrganisationSelected = React.useRef(randomKey());
  const [uState, setUState] = React.useState<APIRequestCreateAsset>({
    name: '',
    organisationId: '',
  });

  const isValid = !!uState.name && !!uState.organisationId;

  const handleSetState = (key: string, value: string) => {
    setUState((cState) => ({ ...cState, [key]: value }));
  };

  const handlePressCreate = React.useCallback(() => {
    Keyboard.dismiss();
    dispatch(REMOTE_CREATE_ASSET(uState));
  }, [dispatch, uState]);

  const hasValues = !!uState.name || !!uState.organisationId;

  const handleClose = React.useCallback(() => {
    if (hasValues) {
      confirmUnsaved({
        onSave: handlePressCreate,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, dispatch, handlePressCreate, hasValues]);

  useEsc(handleClose);

  React.useEffect(() => {
    const events = [
      doAddEvent(eventSelectScreenSelection, (event) => {
        if (event.payload.key === eventKeyOrganisationSelected.current) {
          const organisationId = event.payload.stringValues[0];
          if (organisationId) {
            setUState((cState) => ({
              ...cState,
              organisationId,
            }));
          }
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, []);

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !hasValues });
  }, [navigation, hasValues]);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="New Property"
          onPressCancelButton={handleClose}
          onPressSaveButton={isValid ? handlePressCreate : null}
        />
      }
    >
      <Card
        explanation={
          'Before creating a property, make sure it doesn’t already exist in ' +
          'RiskBase as assessments and other data cannot be moved.'
        }
      />
      <Card
        label="Organisation"
        explanation={
          'Make sure the correct organisation is chosen; the property ' +
          '***cannot*** be moved to a different organisation later'
        }
      >
        <CardRow
          value={
            organisations.find((org) => org.id === uState.organisationId)?.name
          }
          placeholder="Choose an organisation..."
          onPress={() =>
            dispatch(
              NAVIGATE_MODAL({
                screen: ModalScreens.SELECT,
                params: {
                  title: 'Choose Organisation',
                  optionNoun: 'Organisation',
                  showDoneButtonForSingleSelect: false,
                  searchable: true,
                  eventKey: eventKeyOrganisationSelected.current,
                  options: organisations.map((o) => ({
                    name: o.name,
                    internalValue: o.id,
                  })),
                  value: uState.organisationId ? [uState.organisationId] : [],
                },
              }),
            )
          }
        />
      </Card>
      <Card label="Property Name">
        <TextField
          value={uState.name}
          onChange={(value) => handleSetState('name', value)}
          autoCapitalize="words"
          onEsc={handleClose}
        />
      </Card>
    </Page>
  );
};
